import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, MessageSquare, Wrench, LogOut, Loader2,
  Eye, Phone, User as UserIcon, Clock, RefreshCw, Filter,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Νέο', color: 'bg-orange-500' },
  { value: 'in_progress', label: 'Σε εξέλιξη', color: 'bg-blue-500' },
  { value: 'completed', label: 'Ολοκληρωμένο', color: 'bg-green-500' },
  { value: 'cancelled', label: 'Ακυρωμένο', color: 'bg-slate-500' },
];

const StatusBadge = ({ status }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`${opt.color} text-white text-xs font-bold px-3 py-1 rounded`} data-testid={`status-badge-${status}`}>
      {opt.label}
    </span>
  );
};

const formatDate = (s) => {
  try {
    return new Date(s).toLocaleString('el-GR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return s;
  }
};

const AdminDashboard = () => {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('requests'); // requests | sessions | stats
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const config = { withCredentials: true, headers: authHeaders() };
      const [statsRes, reqRes, sesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/service-requests`, config),
        axios.get(`${API}/admin/sessions`, config),
      ]);
      setStats(statsRes.data);
      setRequests(reqRes.data);
      setSessions(sesRes.data);
    } catch (e) {
      toast.error('Σφάλμα φόρτωσης δεδομένων');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/service-requests/${id}`,
        { status: newStatus },
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success('Κατάσταση ενημερώθηκε');
      fetchAll();
    } catch {
      toast.error('Αποτυχία ενημέρωσης');
    }
  };

  const filteredRequests = statusFilter === 'all'
    ? requests
    : requests.filter((r) => r.status === statusFilter);

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-testid="admin-header">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
            <LayoutDashboard className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-wider">ADMIN PANEL</h1>
            <p className="text-slate-400 text-xs">PrimeTech Santorini</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-slate-300 hidden sm:block">{user?.email}</span>
          <button
            onClick={fetchAll}
            data-testid="admin-refresh-btn"
            className="bg-slate-700 hover:bg-slate-600 px-3 py-2 flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Ανανέωση</span>
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="bg-red-600 hover:bg-red-700 px-3 py-2 flex items-center space-x-2"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Αποσύνδεση</span>
          </button>
        </div>
      </header>

      {/* Stats cards */}
      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats">
        {stats && [
          { label: 'Σύνολο', value: stats.total_requests, color: 'border-slate-600' },
          { label: 'Νέα', value: stats.pending, color: 'border-orange-500' },
          { label: 'Σε εξέλιξη', value: stats.in_progress, color: 'border-blue-500' },
          { label: 'Ολοκληρωμένα', value: stats.completed, color: 'border-green-500' },
          { label: 'Συνομιλίες', value: stats.total_sessions, color: 'border-purple-500' },
        ].map((s, i) => (
          <div key={i} className={`bg-slate-800 border-l-4 ${s.color} p-4`}>
            <div className="text-3xl font-extrabold">{s.value}</div>
            <div className="text-slate-400 text-xs uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex space-x-1 border-b border-slate-700 mb-6" data-testid="admin-tabs">
          {[
            { id: 'requests', label: 'Αιτήματα Τεχνικού', icon: Wrench },
            { id: 'sessions', label: 'Συνομιλίες AI', icon: MessageSquare },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-6 py-3 font-bold tracking-wider flex items-center space-x-2 transition-colors ${
                tab === t.id
                  ? 'bg-slate-800 text-orange-500 border-b-2 border-orange-500'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <t.icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* Requests tab */}
        {tab === 'requests' && (
          <div data-testid="admin-requests-list">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="admin-status-filter"
                  className="bg-slate-700 border border-slate-600 text-white px-3 py-2 text-sm"
                >
                  <option value="all">Όλα</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <span className="text-slate-400 text-sm">{filteredRequests.length} αιτήματα</span>
            </div>

            {loading && requests.length === 0 ? (
              <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin mx-auto text-orange-500" /></div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-16 text-slate-500" data-testid="admin-requests-empty">
                Δεν υπάρχουν αιτήματα.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRequests.map((r) => (
                  <div key={r.id} className="bg-slate-800 border border-slate-700 p-5" data-testid={`admin-request-${r.id}`}>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-3 mb-2">
                          <StatusBadge status={r.status} />
                          {r.urgency && (
                            <span className="bg-red-600/20 text-red-400 text-xs font-bold px-2 py-1 rounded uppercase">
                              {r.urgency}
                            </span>
                          )}
                          <span className="text-slate-500 text-xs flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDate(r.created_at)}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                          <div className="flex items-center text-sm">
                            <UserIcon className="w-4 h-4 text-orange-500 mr-2" />
                            <span className="font-bold">{r.contact_name || '-'}</span>
                          </div>
                          <div className="flex items-center text-sm">
                            <Phone className="w-4 h-4 text-orange-500 mr-2" />
                            {r.contact_phone ? (
                              <a href={`tel:${r.contact_phone}`} className="text-orange-400 hover:underline">{r.contact_phone}</a>
                            ) : '-'}
                          </div>
                        </div>
                        {r.equipment_type && (
                          <div className="text-sm text-slate-300 mb-1">
                            <span className="text-slate-500">Μηχάνημα:</span> {r.equipment_type}
                          </div>
                        )}
                        {r.issue_description && (
                          <p className="text-sm text-slate-300 mt-2 line-clamp-3 whitespace-pre-wrap">{r.issue_description}</p>
                        )}
                        {r.notes && (
                          <p className="text-xs text-slate-500 mt-2 italic">Σημειώσεις: {r.notes}</p>
                        )}
                      </div>
                      <div className="flex flex-row md:flex-col gap-2 flex-shrink-0">
                        <select
                          value={r.status}
                          onChange={(e) => updateStatus(r.id, e.target.value)}
                          data-testid={`admin-request-status-${r.id}`}
                          className="bg-slate-700 border border-slate-600 text-white px-3 py-2 text-sm"
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                        {r.session_id && (
                          <Link
                            to={`/admin/sessions/${r.session_id}`}
                            data-testid={`admin-view-session-${r.id}`}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 text-sm flex items-center justify-center space-x-1"
                          >
                            <Eye className="w-4 h-4" />
                            <span>Συνομιλία</span>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sessions tab */}
        {tab === 'sessions' && (
          <div data-testid="admin-sessions-list">
            <div className="mb-4 text-slate-400 text-sm">{sessions.length} συνομιλίες</div>
            {loading && sessions.length === 0 ? (
              <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin mx-auto text-orange-500" /></div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-16 text-slate-500" data-testid="admin-sessions-empty">
                Δεν υπάρχουν συνομιλίες.
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((s) => (
                  <Link
                    key={s.session_id}
                    to={`/admin/sessions/${s.session_id}`}
                    data-testid={`admin-session-${s.session_id}`}
                    className="block bg-slate-800 border border-slate-700 p-5 hover:border-orange-500 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-orange-400">
                        {s.message_count} μηνύματα
                      </span>
                      <span className="text-xs text-slate-500 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatDate(s.last_message_at)}
                      </span>
                    </div>
                    {s.last_user_message && (
                      <p className="text-sm text-slate-300 line-clamp-2 mb-1">
                        <span className="text-slate-500 font-bold">Πελάτης: </span>
                        {s.last_user_message}
                      </p>
                    )}
                    {s.last_assistant_message && (
                      <p className="text-sm text-slate-400 line-clamp-2">
                        <span className="text-slate-500 font-bold">AI: </span>
                        {s.last_assistant_message}
                      </p>
                    )}
                    <p className="text-xs text-slate-600 mt-2 font-mono">{s.session_id}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
